import React, { useEffect, useState } from "react";
import { Badge, Card, DataTable, KpiGrid } from "@netiv/ui-core";

type Horizon = "3M" | "6M" | "1Y" | "2Y" | "3Y" | "5Y";
type Tone = "down" | "warn" | "up";
const HORIZONS: Horizon[] = ["3M", "6M", "1Y", "2Y", "3Y", "5Y"];
const API_BASE = import.meta.env.VITE_API_GATEWAY_URL ?? import.meta.env.VITE_QUERY_API_URL ?? "";

function toneFor(status?: string): Tone {
  return status === "ACT" || status === "High" || status === "Constrained"
    ? "down"
    : status === "READY" || status === "Low" || status === "Available"
      ? "up"
      : "warn";
}

function HorizonSelector({ value, onChange }: { value: Horizon; onChange: (value: Horizon) => void }) {
  return (
    <div className="netiv-ops-horizons" role="tablist" aria-label="Forecast horizon">
      {HORIZONS.map((horizon) => (
        <button
          key={horizon}
          role="tab"
          aria-selected={value === horizon}
          className={`netiv-role-pill ${value === horizon ? "netiv-role-pill--on" : ""}`}
          onClick={() => onChange(horizon)}
        >
          {horizon}
        </button>
      ))}
    </div>
  );
}

export function OperationsOverview() {
  const [horizon, setHorizon] = useState<Horizon>("3M");
  const [overview, setOverview] = useState<{
    categories: { category_id: string; display_name: string; status: string; score?: number }[];
    source_health: { source_system: string; last_success_at?: string; age_seconds?: number; stale: boolean; last_error?: string }[];
    exception_summary: { open: number; critical: number; unattended: number; escalation_required: number };
  } | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setOverview(null);
    setError("");
    fetch(`${API_BASE}/operations/overview?horizon=${horizon}`, { signal: controller.signal, credentials: "include" })
      .then((response) => {
        if (!response.ok) throw new Error(`Operations API returned ${response.status}`);
        return response.json();
      })
      .then(setOverview)
      .catch((reason) => {
        if (reason.name !== "AbortError") setError(reason.message);
      });
    return () => controller.abort();
  }, [horizon]);

  return (
    <>
      <HorizonSelector value={horizon} onChange={setHorizon} />
      {error && <Card title="Data unavailable"><span role="alert" /></Card>}
      {!overview && !error && <Card title="Overview"><span aria-busy="true" /></Card>}
      {overview && (
        <>
          <KpiGrid items={[
            { label: "Open exceptions", value: overview.exception_summary.open, tone: overview.exception_summary.open ? "warn" : "up" },
            { label: "Critical", value: overview.exception_summary.critical, tone: overview.exception_summary.critical ? "down" : "up" },
            { label: "Unattended", value: overview.exception_summary.unattended, tone: overview.exception_summary.unattended ? "down" : "up" },
            { label: "Escalation required", value: overview.exception_summary.escalation_required, tone: overview.exception_summary.escalation_required ? "down" : "up" },
            { label: `Categories · ${horizon}`, value: overview.categories.length },
            { label: "Stale sources", value: overview.source_health.filter((source) => source.stale).length, tone: overview.source_health.some((source) => source.stale) ? "down" : "up" },
          ]} />
          <Card title="Materials and equipment readiness">
            <DataTable
              columns={["Category", "Process", "Readiness", "Score"]}
              rows={overview.categories.map((item) => [
                item.display_name,
                item.category_id.includes("equipment") || item.category_id.includes("construction") || item.category_id.includes("storage") ? "Equipment" : "Materials",
                <Badge tone={toneFor(item.status)}>{item.status}</Badge>,
                item.score == null ? "—" : `${Math.round(item.score * 100)}%`,
              ])}
            />
          </Card>
          <Card title="Source freshness">
            <DataTable
              columns={["Source", "Last successful update", "Age", "State", "Last error"]}
              rows={overview.source_health.map((source) => [
                source.source_system,
                source.last_success_at ? new Date(source.last_success_at).toLocaleString() : "Never",
                source.age_seconds == null ? "—" : `${Math.max(0, Math.round(source.age_seconds / 60))}m`,
                <Badge tone={source.stale ? "down" : "up"}>{source.stale ? "STALE" : "CURRENT"}</Badge>,
                source.last_error ?? "—",
              ])}
            />
          </Card>
        </>
      )}
    </>
  );
}



export function SourceHealth() {
  const [overview, setOverview] = useState<{
    source_health: { source_system: string; last_success_at?: string; age_seconds?: number; stale: boolean; last_error?: string }[];
  } | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API_BASE}/operations/overview?horizon=3M`, { signal: controller.signal, credentials: "include" })
      .then((response) => {
        if (!response.ok) throw new Error(`Operations API returned ${response.status}`);
        return response.json();
      })
      .then(setOverview)
      .catch((reason) => {
        if (reason.name !== "AbortError") setError(reason.message);
      });
    return () => controller.abort();
  }, []);
  if (error) return <Card title="Data unavailable"><span role="alert" /></Card>;
  if (!overview) return <Card title="Source health"><span aria-busy="true" /></Card>;
  return (
    <>
      <KpiGrid items={[
        { label: "Connected sources", value: overview.source_health.length },
        { label: "Current", value: overview.source_health.filter((source) => !source.stale).length, tone: "up" },
        { label: "Stale", value: overview.source_health.filter((source) => source.stale).length, tone: overview.source_health.some((source) => source.stale) ? "down" : "up" },
        { label: "Source errors", value: overview.source_health.filter((source) => source.last_error).length, tone: overview.source_health.some((source) => source.last_error) ? "down" : "up" },
      ]} />
      <Card title="Source freshness">
        <DataTable columns={["Source", "Last successful update", "Age", "State", "Last error"]} rows={overview.source_health.map((source) => [
          source.source_system,
          source.last_success_at ? new Date(source.last_success_at).toLocaleString() : "Never",
          source.age_seconds == null ? "—" : `${Math.max(0, Math.round(source.age_seconds / 60))}m`,
          <Badge tone={source.stale ? "down" : "up"}>{source.stale ? "STALE" : "CURRENT"}</Badge>,
          source.last_error ?? "—",
        ])} />
      </Card>
    </>
  );
}
